import { motion } from 'framer-motion';
import { Award, Heart, Sparkles, ShieldCheck } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="pt-24 pb-32 bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4">
        {/* Story Section */}
        <section className="grid lg:grid-cols-2 gap-20 items-center mb-40">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gold/10 -rotate-3 rounded-[50px]"></div>
            <img
              src="/images/fppic.jpg"
              alt="Moyhe Hair Braiding Origin"
              className="relative rounded-[50px] shadow-premium z-10 w-full object-cover h-[700px]"
            />
            <div className="absolute -bottom-10 -right-10 glass p-10 rounded-[30px] shadow-2xl z-20 border border-white/50">
              <p className="text-gold font-serif text-5xl font-bold tracking-tighter">1995</p>
              <p className="text-[10px] uppercase tracking-[0.3em] font-black text-dark/70">Founded in Charlotte</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="space-y-10"
          >
            <div className="space-y-4">
              <p className="text-gold font-bold uppercase tracking-[0.4em] text-xs">Our Genesis</p>
              <h1 className="text-6xl md:text-8xl font-serif font-bold text-dark leading-tight tracking-tighter">
                A Legacy of <br /><span className="text-gold italic">Pure Excellence</span>
              </h1>
            </div>

            <div className="h-1.5 w-32 bg-gold"></div>

            <p className="text-2xl text-gray-500 leading-relaxed font-light font-serif italic">
              "We didn't just want to create a salon; we wanted to create a sanctuary where heritage is preserved and beauty is elevated to its highest form."
            </p>

            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>Moyhe Hair Braiding was born from a passion for authentic expression. For over three decades, we have served the Charlotte community with a commitment to excellence that goes beyond the surface.</p>
              <p>Our philosophy is simple: Hair is the ultimate protector and the most potent symbol of identity. Every station in our salon is dedicated to honoring that truth with precision, patience, and premium care.</p>
            </div>
          </motion.div>
        </section>

        {/* Values Strip */}
        <section className="bg-dark rounded-[60px] py-24 px-12 relative overflow-hidden mb-40">
          <div className="absolute top-0 right-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.05]"></div>
          <div className="grid md:grid-cols-4 gap-16 relative z-10">
            {[
              { icon: <Award size={32} />, title: "Excellence", desc: "Unmatched technical precision." },
              { icon: <ShieldCheck size={32} />, title: "Integrity", desc: "No hooks, no hidden fees." },
              { icon: <Heart size={32} />, title: "Connection", desc: "Every client is family." },
              { icon: <Sparkles size={32} />, title: "Innovation", desc: "Modern styles, ancient roots." }
            ].map((val, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="text-center space-y-4"
              >
                <div className="inline-block p-4 rounded-2xl bg-gold/10 text-gold mb-4">
                  {val.icon}
                </div>
                <h3 className="text-white font-serif text-2xl font-bold tracking-tight">{val.title}</h3>
                <p className="text-gray-500 text-sm">{val.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Commitment Section */}
        <section className="max-w-4xl mx-auto text-center mb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="p-16 rounded-[40px] border border-gold/20 glass"
          >
            <h2 className="text-4xl md:text-6xl font-serif font-bold text-dark mb-8 tracking-tighter">The Moyhe <span className="text-gold italic">Guarantee</span></h2>
            <p className="text-xl text-gray-500 font-light leading-relaxed mb-10">
              We promise not just a style that lasts, but a session that refreshes. Your health, your comfort, and your confidence are our only metrics of success.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-dark text-gold px-12 py-5 rounded-full font-bold uppercase tracking-widest text-xs shadow-2xl"
            >
              Learn More About Our Techniques
            </motion.button>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
